import {reactFetchJSON} from '@github-ui/verified-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation} from '@github-ui/react-query'

import type {ReviewResponse} from '../page-data/payloads/review-response'
import {conversationCommentsQueryKey} from '../page-data/loaders/use-conversation-comments'
import {usePullRequestBasePath} from '../stores/layout-selectors'
import {Type} from '@github-ui/react-query-persister/typebox'

export const ReviewEvent = {
  approve: 'approve',
  comment: 'comment',
  requestChanges: 'request changes',
} as const

export const ReviewEventSchema = Type.Enum(Object.values(ReviewEvent))

export type ReviewEvent = Type.Static<typeof ReviewEventSchema>

type Input = {
  body: string
  event: ReviewEvent
  headSha: string
}

type Callbacks = {
  onSuccess: (json: ReviewResponse) => void
  onError: (e: Error) => void
}

export function useSubmitReviewMutation({onSuccess, onError}: Callbacks) {
  const apiURL = usePageDataUrl(PageData.submitReview)
  const basePath = usePullRequestBasePath()
  return useMutation({
    mutationFn: async ({body, event, headSha}: Input) => {
      const data = await reactFetchJSON(`${apiURL}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
        },
        body: {body, event, headSha},
      })

      const json = await data.json()
      const errorMessage = json.error || 'Unknown error occurred'
      if (!data.ok) {
        throw new Error(errorMessage)
      }

      return json as ReviewResponse
    },
    onSuccess: (data, variables, _onMutateResult, context) => {
      // TODO: This is obviously not ideal; we'd much rather use pushConversationItemToQueryData to push the new
      // review summary directly to the query data without having to refetch the entire conversation. But the response
      // from the server + what we have available doesn't have enough info to build an accurate conversation item
      // FIXME: The API is better for this now, we just need to update the client to fetch single item
      context.client.invalidateQueries({
        queryKey: conversationCommentsQueryKey(basePath),
        exact: true,
      })
      onSuccess?.(data)
    },
    onError,
  })
}
